import { Button } from '@mui/material';
import { CustomCard } from '../../common/CustomMui';
import { useEffect, useState } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import styled from 'styled-components';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearIcon from '@mui/icons-material/Clear';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import SelectOrderList from '../SelectOrderList';
import {
  backdropAtom,
  backdropTextAtom,
  destInfoModalAtom,
  planDestListAtom,
  selectedOrderListAtom,
  selectOrderModalAtom,
} from '@/commons/constants';
import { Order, PlanDest } from '@/types/plan';
import * as _ from 'lodash';
import { destGrouping } from '../../common/utils';
import DestInfo from './DestInfo';
import { regionAtom } from '@/interfaces/region';

export default function Dest() {
  const [optStr, setOptStr] = useState<string[]>([]);
  return (
    <>
      <CustomCard title={'배송지'} opt={optStr}>
        <DestList setString={setOptStr} />
      </CustomCard>
      <SelectOrderList />
    </>
  );
}

const MainContentWrap = styled.div`
  width: 100%;
  height: 100%;
  flex-grow:1;
  display:flex;
  flex-direction: column;
  .body{
    padding:0;
    width: 100%;
    flex-grow:1
  }
`;

const FilterArea = styled.div`
  width: 100%;
  display:flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
`;

const column: GridColDef[] = [
  { field: 'name', headerName: '배송지', flex: 1, headerAlign: 'center' },
  { field: 'address', headerName: '주소', flex: 2, headerAlign: 'center' },
  { field: 'region', headerName: '권역', width: 80, headerAlign: 'center', align: 'center' },
  { field: 'order_count', headerName: '주문건수', width: 60, headerAlign: 'center', align: 'center' },
  {
    field: 'v_work_time',
    headerName: '배송가능시간',
    width: 150,
    headerAlign: 'center',
    align: 'center',
    renderCell: (param) => {
      const length = param.value.length;
      return (
        <div>
          {param.value[0].start + ' ~ ' + param.value[0].end}
          {length > 1 ? '외 ' + (length - 1) : ''}
        </div>
      );
    },
  },
  { field: 'v_dwell_time', headerName: '체류시간', width: 80, headerAlign: 'center', align: 'center' },
  {
    field: 'coordCheck',
    headerName: '위치확인',
    width: 80,
    headerAlign: 'center',
    align: 'center',
    renderCell: (param?: GridRenderCellParams<any, boolean>) => {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {param.value === false ? <ClearIcon htmlColor="#d77" /> : <DoneAllIcon htmlColor="#7a7" />}
        </div>
      );
    },
  },
];

const DestList = ({ setString }: { setString: any }) => {
  const setBackdrop = useSetAtom(backdropAtom);
  const setBackdropText = useSetAtom(backdropTextAtom);
  const [selectedOrderList, setSelectedOrderList] = useAtom(selectedOrderListAtom);
  const [planDestList, setPlanDestList] = useAtom(planDestListAtom);
  const setSelectOrderMoadl = useSetAtom(selectOrderModalAtom);
  const setDestInfoModal = useSetAtom(destInfoModalAtom);
  const { data: region } = useAtomValue(regionAtom);
  const [list, setList] = useState<PlanDest[]>([]);
  const [contents, setContents] = useState<any[]>();
  const [checkedList, setCheckedList] = useState<PlanDest[]>([]);

  const [modifyList, setModifyList] = useState<PlanDest[]>([]);

  useEffect(() => {
    //Order List -> PlanDestList Grouping
    async function converter() {
      setBackdrop(true);
      setBackdropText('배송지의 위치를 확인중 입니다.');
      try {
        const groupList = await destGrouping(selectedOrderList);
        handleSetList(groupList);
      } finally {
        setBackdrop(false);
        setBackdropText('');
      }
    }
    converter();
  }, [selectedOrderList]);

  useEffect(() => {
    if (modifyList && modifyList.length > 0) {
      let changeFlag = false;
      const temp = selectedOrderList.map((item: Order) => {
        let modifyItem = _.find(modifyList, ['seq', item.seq]);
        if (modifyItem) {
          changeFlag = true;
          const mTemp = _.pick(modifyItem, [
            'region',
            'region_group_seq',
            'lat',
            'lng',
            'sido',
            'sigugun',
            'dongmyun',
            'ri',
            'postal_code',
            'work_time',
            'dwell_time',
            'priority',
            'memo',
          ]);
          modifyItem = {
            ...item,
            ...mTemp,
          };
        }
        return modifyItem ?? item;
      });
      if (changeFlag) {
        setSelectedOrderList(temp);
      }
    }
  }, [modifyList]);
  useEffect(() => {
    if (list && list.length > 0) {
      const temp = _.map(list, (v) => {
        return {
          ...v,
          id: v.group_id,
          v_dwell_time: v.dwell_time / 60 + '분',
          v_work_time: v.work_time,
          coordCheck: v.lat !== null && v.lng !== null ? true : false,
        };
      });
      setContents(temp);
      if (planDestList && planDestList.length > 0) {
        const selectedTemp = _.intersectionBy(temp, planDestList, 'id');
        setPlanDestList(selectedTemp);
      }
    } else {
      setContents([]);
    }
  }, [list]);

  useEffect(() => {
    if (setString) {
      if (list?.length > 0) {
        const str = [
          `총 배송지 : ${checkedList.length}/${list.length}`,
          `총 주문건수 : ${_.sumBy(checkedList, 'order_count')}/${_.sumBy(list, 'order_count')}`,
        ];
        setString(str);
      }
    }
  }, [list, checkedList, setString]);

  const handleSetList = (paramList: PlanDest[]) => {
    const notList: PlanDest[] = _.filter(paramList, ['lat', null]);
    const allowList: PlanDest[] = _.filter(paramList, function (o) {
      return o.lat !== null;
    });
    const temp: PlanDest[] = [...notList, ...allowList];
    setList(temp);
  };

  const handleSelectionModelChange = (val: any) => {
    const temp = val.map((item: number) => {
      return { id: item };
    });
    const target = _.intersectionBy(contents, temp, 'id');
    setCheckedList(target);
    setPlanDestList(target);
  };

  const handleSelectOrderClick = (e: any) => {
    setSelectOrderMoadl(true);
  };

  const handleSelectModifyClick = (e: any) => {
    setModifyList(checkedList);
    setDestInfoModal(true);
  };

  const handleRemoveSelectedClick = () => {
    const removeOrderIds = new Set(checkedList.flatMap((dest) => dest.order_info ?? []));
    setSelectedOrderList(
      (selectedOrderList as any[]).filter((order) => !removeOrderIds.has(order.order_sheet_id)),
    );
    setCheckedList([]);
  };

  const handleRowClick = (item: PlanDest) => {
    setModifyList([item]);
    setDestInfoModal(true);
  };

  return (
    <>
      <MainContentWrap>
        <FilterArea>
          <Button size="small" variant="contained" color="success" onClick={handleSelectOrderClick}>
            주문 불러오기
          </Button>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Button
              disabled={checkedList.length === 0}
              size="small"
              variant="contained"
              color="info"
              onClick={handleSelectModifyClick}
            >
              선택수정
            </Button>
            <Button
              disabled={checkedList.length === 0}
              size="small"
              variant="contained"
              color="error"
              onClick={handleRemoveSelectedClick}
            >
              선택삭제
            </Button>
          </div>
        </FilterArea>
        <DataGrid
          checkboxSelection
          disableColumnResize
          disableRowSelectionOnClick
          columns={column}
          onRowClick={(e) => handleRowClick(e.row)}
          rows={contents ?? []}
          onRowSelectionModelChange={(val) => handleSelectionModelChange(val)}
        />
      </MainContentWrap>
      <DestInfo target={modifyList} setList={setModifyList} />
    </>
  );
};
